import { Component, OnDestroy, OnInit } from '@angular/core';
import { NgForm } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { KanbanColumn } from '@app/interfaces/kanban-column.interface';
import { FunnelTemplateStage } from '@app/models/recruiting/funnel-template-stages.model';
import { FunnelTemplate } from '@app/models/recruiting/funnel-template.model';
import { FunnelTemplateService } from '@app/modules/applicant-tracker/services';
import { NotifyService } from '@app/services';

@Component({
    templateUrl: './funnel-template-update.template.html',
    styleUrls: ['./funnel-template-update.style.scss'],
})
export class FunnelTemplateUpdate implements OnInit, OnDestroy {
    funnelTemplateStages: FunnelTemplateStage[] = [];
    funnelTemplate: FunnelTemplate;
    kanban: KanbanColumn[] = [];

    temporaryColumnName: string;
    isOpenAddFunnel = false;
    isTemplateRenameModalOpen = false;
    renameColumnName = '';
    showRenameBanner = false;
    isLoading = false;

    constructor(
        route: ActivatedRoute,
        public funnelTemplateService: FunnelTemplateService,
        private router: Router,
        private notify: NotifyService
    ) {
        this.funnelTemplate = route.snapshot.data.models.get('funnelTemplate');
    }

    async ngOnInit(): Promise<void> {
        this.isLoading = true;
        this.funnelTemplateStages = await this.loadFunnelStages();
        this.loadKanban();
        this.isLoading = false;
    }

    ngOnDestroy(): void {
        this.notify.success('applicant-tracker.changesToTemplateSavedSuccessfully');
    }

    loadKanban(): void {
        this.kanban = this.funnelTemplateStages.map((funnelTemplateStage: FunnelTemplateStage, index: number) => {
            return { index: index + 1, title: funnelTemplateStage.name, data: funnelTemplateStage };
        });

        // Add applied column
        this.kanban.unshift({ index: 0, title: 'Applied', data: {} });
    }

    async loadFunnelStages(): Promise<FunnelTemplateStage[]> {
        const [funnelTemplateStages] = await FunnelTemplateStage.where('atsFunnelTemplateId', this.funnelTemplate.id)
            .with('funnelMessageTemplate')
            .all();

        funnelTemplateStages.sort((a: FunnelTemplateStage, b: FunnelTemplateStage) => {
            return a.stageIndex - b.stageIndex;
        });

        return funnelTemplateStages;
    }

    closeAddFunnelModal(): void {
        this.isOpenAddFunnel = false;
    }

    openRenameModel(): void {
        this.isTemplateRenameModalOpen = true;
    }

    closeTemplateRenameModal(): void {
        this.isTemplateRenameModalOpen = false;
    }

    onAddColumn(): void {
        this.temporaryColumnName = '';
        this.isOpenAddFunnel = true;
    }

    async onSaveRename(form: NgForm): Promise<void> {
        if (!form.valid) {
            return;
        }

        await this.funnelTemplate.save();
        this.closeTemplateRenameModal();
        this.notify.success({
            key: 'applicant-tracker.templateRenamedToName',
            params: { name: this.funnelTemplate.name },
        });
    }

    async onColumnAdded(form: NgForm): Promise<void> {
        const newFunnelTemplateStage = new FunnelTemplateStage({
            atsFunnelTemplateId: this.funnelTemplate.id,
            name: this.temporaryColumnName,
            stageIndex: this.kanban.length,
        });

        const funnelTemplateStage = await newFunnelTemplateStage.save();

        this.kanban = [
            ...this.kanban,
            { index: this.kanban.length, title: this.temporaryColumnName, data: funnelTemplateStage },
        ];

        this.isOpenAddFunnel = false;
        form.resetForm();
    }

    async onColumnDeleted(funnelTemplateId: number): Promise<void> {
        const updatedKanban: KanbanColumn[] = await this.funnelTemplateService.deleteTemplateFunnelStage(
            funnelTemplateId,
            this.kanban
        );
        // Update local array
        this.kanban = updatedKanban;
    }

    onSaveAllChanges(): void {
        this.router.navigate(['/recruiting/templates/funnelTemplates']);
    }

    onColumnNameUpdate(update: { id: number; name: string }): void {
        this.funnelTemplateService.updateFunnelTemplateColumnName(update, this.kanban);
        this.notify.success({ key: 'applicant-tracker.columnRenamedToName', params: { name: update.name } });
    }
}
